import axios from 'axios';
import md5 from 'md5';

import sessionTimestampManager from 'component-utilities/sessionTimestampManager/sessionTimestampManager';

import reduxStore from '../../reduxStore';
import IfFeature from '../../components/IfFeature';
import { SESSION_EXPIRATION } from '../../components/Modals/modalMap.json';
import PAGES from '../../constants/Pages';

import createUserPayloadString from '../../../features/Authentication/lib/createUserPayloadString';
import { resetFlash, showFlash } from '../FlashMessagesStateManager';
import { openModal } from '../ModalsStateManager';

export const SESSION_EXPIRED: string = 'Session Expired';
export const ALREADY_LOGGED_OUT: string = 'Already logged out';

export const SESSION_EXPIRED_FLASH_ID: string = 'login.session.failure';
export const VALID_FLASH_VIEWS: string[] = [PAGES.LOGIN];

let actions: any = {};

// init
export default function authenticationHelper(store: any = reduxStore) {
  return store.getState().authentication;
}

export function initAuthenticationHelper(authenticationActions: any) {
  actions = authenticationActions;
}

// session expiration manager
export function onShowTimestampModal(authTokenInterval: any) {
  const { sessionsShow, forceLogout } = actions;

  reduxStore.dispatch(
    openModal(SESSION_EXPIRATION, {
      onStillWorking: () => {
        // Ping an API endpoint to reset auth token expiry on backend
        reduxStore.dispatch(sessionsShow());
      },
      onLogMeOut: () => {
        clearInterval(authTokenInterval);

        reduxStore.dispatch(forceLogout());
      },
    }),
  );
}

export function onTimestampLogout() {
  reduxStore.dispatch(showFlash(SESSION_EXPIRED_FLASH_ID, VALID_FLASH_VIEWS));
  reduxStore.dispatch(actions.forceLogout());
}

export const sessionManager = sessionTimestampManager({
  onShowModal: onShowTimestampModal,
  onLogout: onTimestampLogout,
});

// general api requests middleware
export function requestsMiddleware(config: any) {
  const { forceLogout, getLoggedInAs, isLoggedIn } = actions;

  let newConfig = { ...config };

  if (IfFeature.isEnabled('session.manager')) {
    sessionManager.reset(isLoggedIn());
  }

  if (!config.metadata.isOutsideLink && !isLoggedIn()) {
    reduxStore.dispatch(forceLogout());
  }

  if (isLoggedIn()) {
    const loggedInAs = getLoggedInAs();

    newConfig = {
      ...config,
      headers: {
        ...config.headers,
        'X-AuthenticationToken': loggedInAs.authenticationToken,
      },
      params: {
        ...config.params,
        user_payload_hash: md5(createUserPayloadString(loggedInAs)),
      },
    };
  }

  return newConfig;
}

// A request that was aborted or canceled before the server answered is NOT
// evidence that the session is invalid -- the request simply never completed.
// This happens routinely when the browser cancels an in-flight request during a
// page refresh or navigation (e.g. the /sessions/show session check). Such an
// error surfaces as a transport-level failure with no HTTP response:
//   - axios cancellation (CancelToken / AbortController) -> axios.isCancel()
//   - a browser/XHR abort -> code 'ECONNABORTED' and/or message 'Request aborted'
//     (note: axios.isCancel() returns FALSE for these)
//   - any other error where the request was sent but no response came back
// Callers use this to avoid tearing down authentication on an aborted request.
// See SNAR-9828: a canceled session check during refresh was forcing logout.
export function isAbortedRequest(error: any): boolean {
  if (!error) {
    return false;
  }

  if (axios.isCancel(error)) {
    return true;
  }

  if (error.code === 'ECONNABORTED') {
    return true;
  }

  if (error.name === 'CanceledError' || error.name === 'AbortError') {
    return true;
  }

  if (typeof error.message === 'string' && /abort/i.test(error.message)) {
    return true;
  }

  // The request was made but no response was ever received (aborted/network
  // failure), as opposed to a real auth response like 401/422.
  if (error.request && !error.response) {
    return true;
  }

  return false;
}

// general api responses callback
export function responseFailureCallbackBase(error: any) {
  const { forceLogout, isLoggedIn } = actions;

  if ([401, 422].some(status => error.status === status)) {
    let returnValue;

    reduxStore.dispatch(
      resetFlash({
        from: 'AuthenticationStateManagerHelper/responseFailureCallbackBase',
      }),
    );

    switch (error.status) {
      case 401:
      case 422:
        if (isLoggedIn()) {
          returnValue = SESSION_EXPIRED;

          reduxStore.dispatch(forceLogout());
          reduxStore.dispatch(
            showFlash(SESSION_EXPIRED_FLASH_ID, VALID_FLASH_VIEWS),
          );
        } else {
          returnValue = ALREADY_LOGGED_OUT;

          reduxStore.dispatch(forceLogout());
        }

        return returnValue;
      default:
        return `unknown status: ${error.status}`;
    }
  }

  return false;
}

export function responseFailureCallback(error: any) {
  const result = responseFailureCallbackBase(error);

  if (result) {
    throw new Error(result);
  }
}

export function authenticatedResponseFailureCallback() {
  let didFail = false;

  return function handleCallback(error: any) {
    if (!didFail) {
      try {
        responseFailureCallback(error);
      } catch (e) {
        // the failure callback was caught by something we logged the user out
        // of the app from, so we're disabling future checks.
        didFail = true;
      }
    }

    console.log(error);

    return Promise.reject(error);
  };
}